// Manual mana buffs. User-created ledger entries that share the
// EffectLedger plumbing with automation-driven GRANT_MANA auras.
//
// Manual entries get a synthetic sourceCardId ('manual-<uuid>') so the
// engine's removeBySource path (which sweeps when a real card leaves
// play) never touches them. They surface in the HUD buff list alongside
// automation rows but are labelled 'Mana buff' and have no source card.
//
// Each manual entry also carries `durationMeta` recording the user's
// original duration choice. The ledger only needs `expiry` to know
// WHEN to prune; the meta is for display only. Without it, "this
// turn" and "until end of your next turn" both flatten into the same
// turn_end expiry shape and the HUD can't tell them apart.

import { OP } from './schema';

export const MANUAL_BUFF_DURATION = Object.freeze({
  THIS_TURN: 'this_turn',
  NEXT_TURN: 'next_turn',
  N_TURNS:   'n_turns',
  PERMANENT: 'permanent',
});

const VALID_DURATIONS = new Set(Object.values(MANUAL_BUFF_DURATION));

export const N_TURNS_MIN = 1;
export const N_TURNS_MAX = 20;

let _idCounter = 0;
function makeSourceId() {
  _idCounter += 1;
  return `manual-${Date.now().toString(36)}-${_idCounter.toString(36)}`;
}

function clampN(n) {
  if (!Number.isInteger(n)) return N_TURNS_MIN;
  return Math.max(N_TURNS_MIN, Math.min(N_TURNS_MAX, n));
}

// Build the expiry object the EffectLedger understands. Tests pin every
// branch since the math determines when a buff actually drops off.
function buildExpiry({ duration, n, controller, activePlayer, turnNumbers }) {
  if (duration === MANUAL_BUFF_DURATION.PERMANENT) {
    return { type: 'permanent' };
  }
  if (duration === MANUAL_BUFF_DURATION.THIS_TURN) {
    // Expires at the end of whichever turn is currently in progress.
    // _stepPruneExpiredEffects runs at end of `activePlayer`'s turn
    // with their current turnNumber.
    return {
      type: 'turn_end',
      player: activePlayer,
      turnNumber: turnNumbers[activePlayer] || 0,
    };
  }
  if (duration === MANUAL_BUFF_DURATION.NEXT_TURN) {
    // Controller's next turn end. If owner is active, "next turn" is
    // the one after this; if non-active, it's the upcoming one.
    // Either way: controller's turnNumber + 1.
    return {
      type: 'turn_end',
      player: controller,
      turnNumber: (turnNumbers[controller] || 0) + 1,
    };
  }
  if (duration === MANUAL_BUFF_DURATION.N_TURNS) {
    // Persists across N of the CONTROLLER's turn-ends.
    // - If controller is active: current turn counts as turn 1 of N.
    //   Expires at `turnNumbers[controller] + (N - 1)`.
    // - If controller is non-active: first counted turn is the
    //   upcoming one. Expires at `turnNumbers[controller] + N`.
    const count = clampN(n);
    const offset = activePlayer === controller ? count - 1 : count;
    return {
      type: 'turn_end',
      player: controller,
      turnNumber: (turnNumbers[controller] || 0) + offset,
    };
  }
  throw new Error(`Unknown manual-buff duration: ${duration}`);
}

export function createManualManaEntry({
  controller,
  amount,
  duration,
  n,
  activePlayer,
  turnNumbers,
}) {
  if (!VALID_DURATIONS.has(duration)) {
    throw new Error(`Invalid duration: ${duration}`);
  }
  if (!Number.isInteger(amount) || amount === 0) {
    throw new Error(`Invalid amount: ${amount}`);
  }
  const durationMeta = duration === MANUAL_BUFF_DURATION.N_TURNS
    ? { kind: duration, n: clampN(n) }
    : { kind: duration };
  return {
    sourceCardId: makeSourceId(),
    sourceCardName: '',
    controller,
    isManual: true,
    label: 'Mana buff',
    effect: { op: OP.GRANT_MANA, args: { amount } },
    durationMeta,
    expiry: buildExpiry({ duration, n, controller, activePlayer, turnNumbers }),
  };
}

// Human-readable text for the duration column in the HUD buff list.
// Manual entries carry their original durationMeta so the label is
// exact regardless of how the ledger expiry math collapsed. Automation
// entries fall through to a viewer-relative description of the
// turn_end shape.
export function formatExpiry(entry, viewerPlayer) {
  const meta = entry?.durationMeta;
  if (meta?.kind === MANUAL_BUFF_DURATION.PERMANENT) return 'permanent';
  if (meta?.kind === MANUAL_BUFF_DURATION.THIS_TURN) return 'until current turn ends';
  if (meta?.kind === MANUAL_BUFF_DURATION.NEXT_TURN) return 'until end of your next turn';
  if (meta?.kind === MANUAL_BUFF_DURATION.N_TURNS) {
    return `for ${meta.n} of your turn${meta.n === 1 ? '' : 's'}`;
  }

  const exp = entry?.expiry;
  if (!exp || exp.type === 'permanent') return 'permanent';
  if (exp.type === 'until_leaves') return 'while in play';
  if (exp.type !== 'turn_end') return '';
  if (exp.player === viewerPlayer) return 'until your turn ends';
  return "until opponent's turn ends";
}
